import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Raise OPA (Operational Advance / Fund Request) dialog
 *
 * Props:
 *  - target: { type: "task" | "ticket", id, code, subject } | null
 *  - onClose: () => void
 *  - onCreated?: (opaDoc) => void
 */
export default function RaiseOPADialog({ target, onClose, onCreated }) {
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setAmount("");
      setPurpose("");
    }
  }, [target]);

  if (!target) return null;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Please enter an amount greater than 0");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        amount: amt,
        purpose: purpose.trim() || null,
      };
      if (target.type === "task") payload.task_id = target.id;
      else if (target.type === "ticket") payload.issue_id = target.id;

      const { data } = await api.post("/opa-requests", payload);
      toast.success(`OPA raised — ${data.code} (pending approval)`);
      onCreated && onCreated(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to raise OPA");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-md"
        data-testid="raise-opa-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Wallet className="w-5 h-5" /> Raise OPA Fund Request
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Operational Advance for{" "}
            <span className="font-mono text-sky-400">{target.code}</span>
            {target.subject && <> — {target.subject}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs text-zinc-400">Amount (₹) <span className="text-red-400">*</span></Label>
            <Input
              type="number"
              min="1"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g., 5000"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="raise-opa-amount"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Purpose / Justification</Label>
            <Textarea
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="What is this advance for?"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white min-h-[90px]"
              data-testid="raise-opa-purpose"
            />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            className="text-zinc-400 hover:text-white"
            data-testid="raise-opa-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-white text-black hover:bg-zinc-200 font-medium"
            data-testid="raise-opa-submit"
          >
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Submit for Approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
